/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { Chart } from 'react-google-charts';

import {
  format,
  parseISO,
  endOfMonth,
  addMonths,
  eachMonthOfInterval,
} from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import { formatPrice, TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import SelectSync from '../../../components/SelectSync';
import Loading from '../../../components/Loading';

import getValidationErrors from '../../../utils/getValidationErrors';

import { RelatorioHeader, ContainerResumo, ContainerFlexWrap } from './styles';
import getResponseErrors from '../../../utils/getResponseErrors';
import PageInfo from '../../../components/PageInfo';

interface IItemFormaPagamento {
  descricao: string;
  total: number;
  totalFormatado: string;
}

interface IRetornoResumoGerencial {
  CliCodigo: number;
  CliNome: string;
  totalConsultas: number;
  totalConsultasFormatado: string;
  totalCartaoSaude: number;
  totalCartaoSaudeFormatado: string;
  totalDespesas: number;
  totalDespesasFormatado: string;
  totalOutrasReceitas?: number;
  totalOutrasReceitasFormatado?: string;
  totalReceitasFormatado: string;
  totalReceitaDespesaFormatado: string;
  formaPagamento: IItemFormaPagamento[];
  formaPagamentoPorCategoria: {
    consulta: IItemFormaPagamento[];
    exame: IItemFormaPagamento[];
    cartao: IItemFormaPagamento[];
  };
}

interface ITotais {
  totalConsultasFormatado: string;
  totalCartaoSaudeFormatado: string;
  totalDespesasFormatado: string;
  totalOutrasReceitasFormatado?: string;
  totalReceitasFormatado: string;
  totalReceitaDespesaFormatado: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
  PerCodigo: string;
  ConUsuCodigo: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

export interface IItemResumoMes {
  descricao: string;
  mes: string;
  total: number;
}

interface IRetornoResumoGerencialAcumulado {
  CliCodigo: number;
  CliNome: string;
  valores: IItemResumoMes[];
}

interface IGenericType {
  [key: string]: any;
}

interface IGraficos {
  clinica: string;
  graficoReceitaDespesa: IGenericType[];
  graficoConsultaExame: IGenericType[];
  graficoAtendimento: IGenericType[];
}

const ResumoGerencial: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user, clinica: ClinicaLogada } = useAuth();
  const { optionsClinica, imagensClinica } = useClinica();

  const { CliDrSaude } = ClinicaLogada;

  const logo =
    imagensClinica.find(img => img.nome === 'logoRelatorio')?.arquivo_url || '';

  const formRefBusca = useRef<FormHandles>(null);
  const formRefBuscaAcumulado = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<
    IRetornoResumoGerencial[]
  >();
  const [totais, setTotais] = useState<ITotais>();
  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
  };

  const initialDataAcumulado = {
    dataInicio: format(addMonths(new Date(), -6), 'yyyy-MM'),
    dataFim: format(new Date(), 'yyyy-MM'),
    CliCodigo: user.UsuClinica.toString(),
  };

  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  const [dataExibeAcumulado, setDataExibeAcumulado] = useState<IDataExibe>();
  const [clinica, setClinica] = useState('');

  const [graficos, setGraficos] = useState<IGraficos[]>();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const informacoesPagina = `<b>Descrição:</b> Resumo de todos os lançamentos financeiros, receita e despesa de um determinado período.<br />Gráficos com acumulado mês a mês das Receitas x Despesas, comparativo de receita de Agendamentos x Cartão Saúde e número de atendimentos`;

  useEffect(() => {
    formRefBusca.current?.submitForm();
    formRefBuscaAcumulado.current?.submitForm();
  }, []);

  const handleSubmitBuscaAcumulado = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBuscaAcumulado.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = new Date(
          `${dados.dataInicio.toString()}-01 04:00:00`,
        );
        const dataFimSelecionada = endOfMonth(
          new Date(`${dados.dataFim.toString()}-01 04:00:00`),
        );

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibeAcumulado({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const parametrosOpcionais = dados.CliCodigo
          ? `&CliCodigo=${dados.CliCodigo}`
          : '';

        const response = await api.get<IRetornoResumoGerencialAcumulado[]>(
          `/relatorios/resumogerencialacumulado?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${parametrosOpcionais}`,
        );

        const meses = eachMonthOfInterval({
          start: dataInicioSelecionada,
          end: addMonths(dataFimSelecionada, -1),
        }).map(data => format(data, 'MM-yyyy'));

        const graficoReceitaDespesaLegenda: IGenericType[] = [
          ['Mes', 'Receitas', 'Despesas'],
        ];

        const graficoAtendimentoLegenda: IGenericType[] = [
          ['Mes', 'Atendimentos'],
        ];

        const graficoConsultaLegenda: IGenericType[] = [
          ['Mes', 'Consultas', 'Cartao', { role: 'style' }],
        ];

        const graficoGeral: IGraficos[] = [];

        response.data.forEach(rel => {
          const resultado: IGenericType[] = [];
          const receitaDespesa: IGenericType[] = [];
          const atendimentosT: IGenericType[] = [];
          meses.forEach(mes => {
            const mesFiltrado = rel.valores.filter(val => val.mes === mes);
            const consulta =
              mesFiltrado.find(val => val.descricao === 'consulta')?.total || 0;
            const cartaoSaude =
              mesFiltrado.find(val => val.descricao === 'cartaoSaude')?.total ||
              0;
            const outrasReceitas =
              mesFiltrado.find(val => val.descricao === 'outrasReceitas')
                ?.total || 0;
            const despesas =
              mesFiltrado.find(val => val.descricao === 'despesas')?.total || 0;
            const atendimentos =
              mesFiltrado.find(val => val.descricao === 'atendimentos')
                ?.total || 0;
            resultado.push([mes, consulta, cartaoSaude, '#FFD700']);
            receitaDespesa.push([
              mes,
              consulta + cartaoSaude + (CliDrSaude ? outrasReceitas : 0),
              despesas,
            ]);
            atendimentosT.push([mes, atendimentos]);
          });

          graficoGeral.push({
            clinica: rel.CliNome,
            graficoConsultaExame: graficoConsultaLegenda.concat(resultado),
            graficoReceitaDespesa: graficoReceitaDespesaLegenda.concat(
              receitaDespesa,
            ),
            graficoAtendimento: graficoAtendimentoLegenda.concat(atendimentosT),
          });
        });

        setGraficos(graficoGeral);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBuscaAcumulado.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, CliDrSaude],
  );

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setClinica(
          optionsClinica.find(cli => cli.value === dados.CliCodigo)?.label ||
            '',
        );

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const parametrosOpcionais = dados.CliCodigo
          ? `&CliCodigo=${dados.CliCodigo}`
          : '';

        const response = await api.get<IRetornoResumoGerencial[]>(
          `/relatorios/resumogerencial?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${parametrosOpcionais}`,
        );

        let totalCartaoSaudeGeral = 0;
        let totalConsultasGeral = 0;
        let totalDespesasGeral = 0;
        let totalOutrasReceitasGeral = 0;
        let totalReceitasGeral = 0;
        let totalReceitaDespesaGeral = 0;

        const consultasFormatado = response.data.map(rel => {
          const totalReceitas =
            rel.totalCartaoSaude +
            rel.totalConsultas +
            (rel.totalOutrasReceitas || 0);
          const totalReceitaDespesa = totalReceitas - rel.totalDespesas;

          totalCartaoSaudeGeral += rel.totalCartaoSaude;
          totalConsultasGeral += rel.totalConsultas;
          totalDespesasGeral += rel.totalDespesas;
          if (rel.totalOutrasReceitas)
            totalOutrasReceitasGeral += rel.totalOutrasReceitas;
          totalReceitasGeral += totalReceitas;
          totalReceitaDespesaGeral += totalReceitaDespesa;

          return {
            ...rel,
            totalCartaoSaudeFormatado: formatPrice(rel.totalCartaoSaude),
            totalConsultasFormatado: formatPrice(rel.totalConsultas),
            totalDespesasFormatado: formatPrice(rel.totalDespesas),
            totalOutrasReceitasFormatado: formatPrice(
              rel.totalOutrasReceitas || 0,
            ),
            totalReceitasFormatado: formatPrice(totalReceitas),
            totalReceitaDespesaFormatado: formatPrice(totalReceitaDespesa),
            formaPagamento:
              rel.formaPagamento.length > 0
                ? [
                    ...rel.formaPagamento.map(forma => {
                      return {
                        ...forma,
                        totalFormatado: formatPrice(forma.total),
                      };
                    }),
                    {
                      descricao: 'TOTAL',
                      totalFormatado: formatPrice(
                        rel.formaPagamento.reduce((tot, item) => {
                          return tot + Number(item.total);
                        }, 0),
                      ),
                      total: rel.formaPagamento.reduce((tot, item) => {
                        return tot + Number(item.total);
                      }, 0),
                    },
                  ]
                : [],
            formaPagamentoPorCategoria: {
              consulta:
                rel.formaPagamentoPorCategoria.consulta.length > 0
                  ? [
                      ...rel.formaPagamentoPorCategoria.consulta.map(forma => {
                        return {
                          ...forma,
                          totalFormatado: formatPrice(forma.total),
                        };
                      }),
                      {
                        descricao: 'TOTAL',
                        totalFormatado: formatPrice(
                          rel.formaPagamentoPorCategoria.consulta.reduce(
                            (tot, item) => {
                              return tot + Number(item.total);
                            },
                            0,
                          ),
                        ),
                        total: rel.formaPagamentoPorCategoria.consulta.reduce(
                          (tot, item) => {
                            return tot + Number(item.total);
                          },
                          0,
                        ),
                      },
                    ]
                  : [],
              exame:
                rel.formaPagamentoPorCategoria.exame.length > 0
                  ? [
                      ...rel.formaPagamentoPorCategoria.exame.map(forma => {
                        return {
                          ...forma,
                          totalFormatado: formatPrice(forma.total),
                        };
                      }),
                      {
                        descricao: 'TOTAL',
                        totalFormatado: formatPrice(
                          rel.formaPagamentoPorCategoria.exame.reduce(
                            (tot, item) => {
                              return tot + Number(item.total);
                            },
                            0,
                          ),
                        ),
                        total: rel.formaPagamentoPorCategoria.exame.reduce(
                          (tot, item) => {
                            return tot + Number(item.total);
                          },
                          0,
                        ),
                      },
                    ]
                  : [],
              cartao:
                rel.formaPagamentoPorCategoria.cartao.length > 0
                  ? [
                      ...rel.formaPagamentoPorCategoria.cartao.map(forma => {
                        return {
                          ...forma,
                          totalFormatado: formatPrice(forma.total),
                        };
                      }),
                      {
                        descricao: 'TOTAL',
                        totalFormatado: formatPrice(
                          rel.formaPagamentoPorCategoria.cartao.reduce(
                            (tot, item) => {
                              return tot + Number(item.total);
                            },
                            0,
                          ),
                        ),
                        total: rel.formaPagamentoPorCategoria.cartao.reduce(
                          (tot, item) => {
                            return tot + Number(item.total);
                          },
                          0,
                        ),
                      },
                    ]
                  : [],
            },
          };
        });

        setDadosRelatorio(consultasFormatado);
        setTotais({
          totalCartaoSaudeFormatado: formatPrice(totalCartaoSaudeGeral),
          totalConsultasFormatado: formatPrice(totalConsultasGeral),
          totalDespesasFormatado: formatPrice(totalDespesasGeral),
          totalOutrasReceitasFormatado: formatPrice(totalOutrasReceitasGeral),
          totalReceitasFormatado: formatPrice(totalReceitasGeral),
          totalReceitaDespesaFormatado: formatPrice(totalReceitaDespesaGeral),
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, optionsClinica],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}

      <div className="bg-white border p-2 my-3 d-flex justify-content-between align-items-center">
        <img src={logo} alt="Agenda facil" />
        <h2 id="topoBusca">Resumo Gerencial</h2>
        <div className="d-flex justify-content-between align-items-center">
          <span className="pr-2">
            Impresso em
            {dataExibe?.dataHora}{' '}
          </span>
          <PageInfo
            descricao={informacoesPagina}
            permissao={TIPO_USUARIO.SUPERVISOR}
          />
        </div>
      </div>

      <RelatorioHeader>
        Resumo Gerencial {`- ${clinica} -`} {dataExibe?.dataDiaSemana}
      </RelatorioHeader>
      <div className="bg-white border p-2">
        {optionsClinica.length > 0 && user && (
          <Form
            ref={formRefBusca}
            initialData={initialData}
            onSubmit={handleSubmitBusca}
            className="mb-2 d-print-none"
          >
            <Row>
              <Col md={2}>
                <Input name="dataInicio" label="De" type="date" required />
              </Col>
              <Col md={2}>
                <Input name="dataFim" label="Até" type="date" required />
              </Col>
              <Col md={2}>
                <SelectSync
                  name="CliCodigo"
                  label="Clinica"
                  isDisabled={user.UsuTipo > TIPO_USUARIO.ADMINISTRADOR}
                  isClearable={user.UsuTipo === TIPO_USUARIO.ADMINISTRADOR}
                  options={optionsClinica}
                  handleSelectChange={handleOnChangeVazio}
                />
              </Col>
              <Col md={2}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        )}
        <div className="d-block" style={{ overflowX: 'auto' }}>
          {dadosRelatorio && (
            <>
              {dadosRelatorio.map(rel => (
                <React.Fragment key={`clinica-${rel.CliCodigo}`}>
                  <h5 style={{ textAlign: 'center' }}>{rel.CliNome}</h5>
                  <ContainerResumo>
                    <table
                      className="table table-hover table-sm table-font-small table-bordered"
                      style={{ borderWidth: '0px' }}
                    >
                      <thead>
                        <tr className="text-center">
                          <th>Tipo</th>
                          <th style={{ minWidth: '70px' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Consulta/Exame</td>
                          <td style={{ textAlign: 'right' }}>
                            {rel.totalConsultasFormatado}
                          </td>
                        </tr>
                        <tr>
                          <td>Cartão Saúde</td>
                          <td style={{ textAlign: 'right' }}>
                            {rel.totalCartaoSaudeFormatado}
                          </td>
                        </tr>
                        {CliDrSaude && (
                          <tr>
                            <td>Outras Receitas</td>
                            <td style={{ textAlign: 'right' }}>
                              {rel.totalOutrasReceitasFormatado}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>Total Receitas</td>
                          <td style={{ textAlign: 'right' }}>
                            {rel.totalReceitasFormatado}
                          </td>
                        </tr>
                        <tr>
                          <td>Despesas(-)</td>
                          <td style={{ textAlign: 'right' }}>
                            {rel.totalDespesasFormatado}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2}>
                            <br />
                          </td>
                        </tr>
                        <tr>
                          <td>Total</td>
                          <td style={{ textAlign: 'right' }}>
                            {rel.totalReceitaDespesaFormatado}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {rel.formaPagamento.length > 0 && (
                      <table
                        className="table table-hover table-sm table-font-small table-bordered"
                        style={{ borderWidth: '0px' }}
                      >
                        <thead>
                          <tr className="text-center">
                            <th>Forma Pagamento</th>
                            <th style={{ minWidth: '70px' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rel.formaPagamento.map(pag => (
                            <tr key={`formaP-${rel.CliCodigo}${pag.descricao}`}>
                              <td>{pag.descricao}</td>
                              <td style={{ textAlign: 'right' }}>
                                {pag.totalFormatado}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {rel.formaPagamentoPorCategoria.consulta.length > 0 && (
                      <table
                        className="table table-hover table-sm table-font-small table-bordered"
                        style={{ borderWidth: '0px' }}
                      >
                        <thead>
                          <tr className="text-center">
                            <th>Pagamento Consulta</th>
                            <th style={{ minWidth: '70px' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rel.formaPagamentoPorCategoria.consulta.map(pag => (
                            <tr
                              key={`formaPC-${rel.CliCodigo}${pag.descricao}`}
                            >
                              <td>{pag.descricao}</td>
                              <td style={{ textAlign: 'right' }}>
                                {pag.totalFormatado}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {rel.formaPagamentoPorCategoria.exame.length > 0 && (
                      <table
                        className="table table-hover table-sm table-font-small table-bordered"
                        style={{ borderWidth: '0px' }}
                      >
                        <thead>
                          <tr className="text-center">
                            <th>Pagamento Exames</th>
                            <th style={{ minWidth: '70px' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rel.formaPagamentoPorCategoria.exame.map(pag => (
                            <tr
                              key={`formaPC-${rel.CliCodigo}${pag.descricao}`}
                            >
                              <td>{pag.descricao}</td>
                              <td style={{ textAlign: 'right' }}>
                                {pag.totalFormatado}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {rel.formaPagamentoPorCategoria.cartao.length > 0 && (
                      <table
                        className="table table-hover table-sm table-font-small table-bordered"
                        style={{ borderWidth: '0px' }}
                      >
                        <thead>
                          <tr className="text-center">
                            <th>Pagamento Cartão</th>
                            <th style={{ minWidth: '70px' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rel.formaPagamentoPorCategoria.cartao.map(pag => (
                            <tr
                              key={`formaPC-${rel.CliCodigo}${pag.descricao}`}
                            >
                              <td>{pag.descricao}</td>
                              <td style={{ textAlign: 'right' }}>
                                {pag.totalFormatado}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </ContainerResumo>
                </React.Fragment>
              ))}
              {dadosRelatorio?.length > 1 && (
                <>
                  <h5 style={{ textAlign: 'center' }}>Total Geral</h5>
                  <ContainerResumo>
                    <table
                      className="table table-hover table-sm table-font-small table-bordered"
                      style={{ borderWidth: '0px' }}
                    >
                      <thead>
                        <tr className="text-center">
                          <th>Tipo</th>
                          <th style={{ minWidth: '70px' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Consulta/Exame</td>
                          <td style={{ textAlign: 'right' }}>
                            {totais?.totalConsultasFormatado}
                          </td>
                        </tr>
                        <tr>
                          <td>Cartão Saúde</td>
                          <td style={{ textAlign: 'right' }}>
                            {totais?.totalCartaoSaudeFormatado}
                          </td>
                        </tr>
                        {CliDrSaude && (
                          // eslint-disable-next-line react/jsx-indent
                          <tr>
                            <td>Outras Receitas</td>
                            <td style={{ textAlign: 'right' }}>
                              {totais?.totalOutrasReceitasFormatado}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>Total Receitas</td>
                          <td style={{ textAlign: 'right' }}>
                            {totais?.totalReceitasFormatado}
                          </td>
                        </tr>
                        <tr>
                          <td>Despesas(-)</td>
                          <td style={{ textAlign: 'right' }}>
                            {totais?.totalDespesasFormatado}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2}>
                            <br />
                          </td>
                        </tr>
                        <tr>
                          <td>Total</td>
                          <td style={{ textAlign: 'right' }}>
                            {totais?.totalReceitaDespesaFormatado}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </ContainerResumo>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <RelatorioHeader className="mt-2">
        Acumulado Mensal {`- ${clinica} -`} {dataExibeAcumulado?.dataDiaSemana}
      </RelatorioHeader>
      <div className="bg-white border p-2">
        <div className="d-print-none">
          {optionsClinica.length > 0 && user && (
            <Form
              ref={formRefBuscaAcumulado}
              initialData={initialDataAcumulado}
              onSubmit={handleSubmitBuscaAcumulado}
            >
              <Row>
                <Col md={3}>
                  <Input name="dataInicio" label="De" type="month" required />
                </Col>
                <Col md={3}>
                  <Input name="dataFim" label="Até" type="month" required />
                </Col>
                <Col md={2}>
                  <SelectSync
                    name="CliCodigo"
                    label="Clinica"
                    isDisabled={user.UsuTipo > TIPO_USUARIO.ADMINISTRADOR}
                    isClearable={user.UsuTipo === TIPO_USUARIO.ADMINISTRADOR}
                    options={optionsClinica}
                    handleSelectChange={handleOnChangeVazio}
                  />
                </Col>
                <Col md={2}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        {graficos &&
          graficos.map(cli => (
            <ContainerFlexWrap>
              <div>
                <Chart
                  key={`grafRec-${cli.clinica}`}
                  chartType="ColumnChart"
                  data={cli.graficoReceitaDespesa}
                  options={{
                    title: `Receita X Despesas ${cli.clinica}`,
                    legend: 'none',
                  }}
                  width="100%"
                  height="300px"
                  legendToggle
                />
              </div>
              <div>
                <Chart
                  key={`grafCon-${cli.clinica}`}
                  chartType="ColumnChart"
                  data={cli.graficoConsultaExame}
                  options={{
                    title: `Consultas e Exames X Cartão Saúde ${cli.clinica}`,
                    legend: 'none',
                  }}
                  width="100%"
                  height="300px"
                  legendToggle
                />
              </div>
              <div>
                <Chart
                  key={`graf-${cli.clinica}`}
                  chartType="ColumnChart"
                  data={cli.graficoAtendimento}
                  options={{
                    title: `Atendimentos ${cli.clinica}`,
                    legend: 'none',
                  }}
                  width="100%"
                  height="300px"
                  legendToggle
                />
              </div>
            </ContainerFlexWrap>
          ))}
      </div>
    </Container>
  );
};

export default ResumoGerencial;
